/**
 * SAISEN — "1 poêle achetée = 1 couvercle offert" cart sync.
 *
 * What this does NOT do: it never sets a price to 0€ itself. The actual
 * discount (34,90€ -> 0€) must come from a real Shopify automatic discount
 * (Buy X Get Y, configured in Shopify admin -> Discounts). This script only
 * keeps the *quantity* of the real gift product line in the cart equal to
 * the total quantity of the trigger product (the pan), across every size/
 * variant, so the discount has a matching item to apply to. Everything the
 * customer eventually pays is computed by Shopify itself from real product
 * prices and real discounts — never hardcoded here.
 *
 * How it stays in sync from *any* entry point (the page's own "Ajouter au
 * panier" button/accordion, the cart drawer's quantity +/- buttons, the
 * trash icon, the "-" at minimum quantity removal): rather than hooking
 * every individual button, this wraps the one thing all of them ultimately
 * go through — the standard Shopify cart AJAX endpoints — and reconciles
 * the cart state after every call.
 *
 * A customer who manually adds the gift product on its own (from its own
 * product page, with no pan in the cart) is left alone: this script only
 * ever creates/adjusts/removes the ONE line it tags with the
 * "_saisen_free_gift" property, and never touches a line without that tag.
 * That manually-added line keeps its normal price.
 */
(function () {
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var GIFT_PROPERTY_KEY = '_saisen_free_gift';

  var nativeFetch = window.fetch.bind(window);
  var config = null; // resolved lazily once the cart-drawer-items root exists
  var reconciling = false;
  var rerunQueued = false;

  function readConfig() {
    var root = document.querySelector('[data-saisen-gift-sync-enabled="true"]');
    if (!root) return null;

    var triggerProductId = parseInt(root.dataset.saisenGiftTriggerProductId, 10);
    var giftProductId = parseInt(root.dataset.saisenGiftProductId, 10);
    var giftVariantId = parseInt(root.dataset.saisenGiftVariantId, 10);
    if (!triggerProductId || !giftProductId || !giftVariantId) return null;

    return { triggerProductId: triggerProductId, giftProductId: giftProductId, giftVariantId: giftVariantId };
  }

  function getCartUi() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function syncEmptyClass(isEmpty) {
    // renderContents() does not reliably toggle "is-empty" on <cart-drawer>
    // itself (the theme's own cart-discount-code code, in
    // layout/theme.liquid, has the exact same explicit workaround). Left
    // stale, base.css collapses cart-drawer.is-empty .cart-drawer__body to
    // height:0;overflow:hidden — the newly rendered item markup is present
    // but invisible.
    var drawerEl = document.querySelector('cart-drawer');
    if (!drawerEl) return;
    drawerEl.classList.toggle('is-empty', !!isEmpty);
  }

  function refreshCartUi(sections, isEmpty) {
    var cartUi = getCartUi();
    if (cartUi && typeof cartUi.renderContents === 'function' && sections) {
      cartUi.renderContents({ sections: sections });
      syncEmptyClass(isEmpty);
    }
  }

  async function reconcile() {
    if (reconciling) {
      rerunQueued = true;
      return;
    }
    reconciling = true;

    try {
      if (!config) config = readConfig();
      if (!config) return; // feature not configured (or disabled) yet

      var cart = await (await nativeFetch('/cart.js')).json();

      var panQty = 0;
      var giftLine = null;
      var giftLineIndex = null;

      cart.items.forEach(function (item, index) {
        if (item.product_id === config.triggerProductId) {
          panQty += item.quantity;
        }
        if (
          item.product_id === config.giftProductId &&
          item.properties &&
          String(item.properties[GIFT_PROPERTY_KEY]) === 'true'
        ) {
          giftLine = item;
          giftLineIndex = index + 1; // /cart/change.js "line" is 1-based
        }
      });

      var currentGiftQty = giftLine ? giftLine.quantity : 0;
      if (currentGiftQty === panQty) return; // already in sync, nothing to do

      var response;
      if (giftLine) {
        // Covers both "increase" (more pans added) and "decrease/remove"
        // (pan quantity reduced or pan removed) by simply setting the exact
        // target quantity on the existing tagged gift line.
        response = await nativeFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: giftLineIndex,
            quantity: panQty,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });
      } else if (panQty > 0) {
        // No gift line yet and at least one pan in cart: create it, tagged
        // so it is never confused with a gift the customer bought themself.
        var properties = {};
        properties[GIFT_PROPERTY_KEY] = 'true';
        response = await nativeFetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: config.giftVariantId, quantity: panQty, properties: properties }],
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });
      } else {
        return; // no pans, no existing gift line: nothing to do
      }

      var data = await response.json();
      if (data && data.sections) {
        // /cart/change.js returns the full cart object (has item_count).
        // /cart/add.js (items[] format) does not, but a successful add of
        // panQty > 0 units always makes the cart non-empty regardless.
        var isEmpty = typeof data.item_count === 'number' ? data.item_count === 0 : panQty === 0;
        refreshCartUi(data.sections, isEmpty);
      }
    } catch (error) {
      console.error('SAISEN gift sync: failed to reconcile free gift quantity.', error);
    } finally {
      reconciling = false;
      if (rerunQueued) {
        rerunQueued = false;
        reconcile();
      }
    }
  }

  function patchFetch() {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = nativeFetch.apply(window, arguments);
      if (CART_MUTATION_RE.test(url)) {
        promise.then(function () {
          reconcile();
        });
      }
      return promise;
    };
  }

  function start() {
    config = readConfig();
    if (!config) return; // not configured: do nothing, touch nothing

    patchFetch();
    reconcile(); // fix up a cart that may already be out of sync on load
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
